import { Bar } from '@modules/charts/components/BarChartWrapper/Bar';
import { BarChartResizeHandle } from '@modules/charts/components/BarChartWrapper/BarChartResizeHandle';
import {
  BarChartXAxisProps,
  BarChartYAxisProps,
  RenderBarParams,
} from '@modules/charts/types';
import {
  getBarChartDomainFromMinMaxValues,
  getChartLabelIdOrName,
} from '@modules/charts/utils';
import { TypographyWithEllipsis } from '@modules/common/components/TypographyWithEllipsis';
import { GetChartLabelOptions } from '@modules/common/types';
import { Box, Stack } from '@mui/material';
import { useRef, useState } from 'react';
import { useMeasure } from 'react-use';

export const MIN_LABEL_WIDTH_IN_PIXELS = 40;
const MAX_LABEL_WIDTH_PERCENTAGE = 80;

export const BAR_LABEL_TYPOGRAPHY_VARIANT = 'caption';

interface BarGroupProps<T> {
  data: T[];
  minHeight?: number;
  renderBar?: (params: RenderBarParams<T>) => React.ReactNode;
  xAxis: BarChartXAxisProps<T>;
  yAxis: BarChartYAxisProps<T>;
  getChartColor?: (index: number, sentiment: string) => string;
  getLabelOptions?: GetChartLabelOptions;
  canBeResized?: boolean;
  defaultLabelWidth: number;
  onMouseEnter?: (event: React.MouseEvent, index: number) => void;
  onMouseLeave?: (event: React.MouseEvent) => void;
  onResizeComplete?: (labelWidthPercentage: number) => void;
  formatBarValueLabel?: (value: number) => string;
}

export const BarGroup = <T extends { id: string }>({
  data,
  renderBar,
  xAxis,
  yAxis,
  getChartColor,
  getLabelOptions,
  canBeResized = true,
  minHeight,
  defaultLabelWidth,
  onMouseEnter,
  onMouseLeave,
  onResizeComplete,
  formatBarValueLabel,
}: BarGroupProps<T>) => {
  const [resizedLabelWidth, setResizedLabelWidth] = useState<number>();
  const resizedLabelWidthRef = useRef<number | undefined>(resizedLabelWidth); // used to avoid stale closure in 'onResizeComplete'

  const [isResizing, setIsResizing] = useState(false);

  const [barGroupRef, { width: barGroupWidth }] = useMeasure();

  const getXAxisDomain = (): [number, number] => {
    if (xAxis.domain) {
      return xAxis.domain;
    }

    const values = data.map((dataPoint) =>
      typeof dataPoint[xAxis.dataKey] === 'number'
        ? Number(dataPoint[xAxis.dataKey])
        : 0,
    );

    const maxValue = Math.max(...values);
    const minValue = Math.min(...values);

    return getBarChartDomainFromMinMaxValues({
      min: minValue,
      max: maxValue,
    });
  };

  const updatedXAxis = {
    ...xAxis,
    domain: getXAxisDomain(),
  };

  const barGroupHeight = xAxis?.label && minHeight ? minHeight - 70 : '100%';
  const shouldShowResizeHandle = canBeResized && yAxis?.tick !== false;
  const shouldShowLabel = yAxis?.tick !== false;

  const labelWidthInPixels = resizedLabelWidth || defaultLabelWidth;
  const labelWidthPercentage = (labelWidthInPixels / barGroupWidth) * 100;

  const isLabelWidthPercentageExceeded =
    labelWidthPercentage > MAX_LABEL_WIDTH_PERCENTAGE;

  const labelWidthToUse = isLabelWidthPercentageExceeded
    ? barGroupWidth * (MAX_LABEL_WIDTH_PERCENTAGE / 100)
    : labelWidthInPixels;

  const onResizeLabelWidth = (widthDifference: number) => {
    const newWidthInPixels = labelWidthToUse + widthDifference;
    const isDecreasing = newWidthInPixels < labelWidthToUse;

    if (isDecreasing && newWidthInPixels < MIN_LABEL_WIDTH_IN_PIXELS) {
      return;
    }

    const isIncreasing = newWidthInPixels > labelWidthToUse;
    const newWidthPercentage = (newWidthInPixels / barGroupWidth) * 100;

    if (isIncreasing && newWidthPercentage > MAX_LABEL_WIDTH_PERCENTAGE) {
      return;
    }

    setResizedLabelWidth(newWidthInPixels);
    resizedLabelWidthRef.current = newWidthInPixels;
  };

  const onResizeHandleMouseDown = () => {
    setIsResizing(true);
  };

  const onResizeHandleMouseUp = () => {
    setIsResizing(false);
    if (resizedLabelWidthRef.current) {
      onResizeComplete?.(resizedLabelWidthRef.current);
    }
  };

  return (
    <Box
      width="100%"
      height={barGroupHeight}
      ref={barGroupRef}
      sx={{
        userSelect: isResizing ? 'none' : 'auto', // Prevents text selection while resizing
      }}
    >
      <Stack
        height="100%"
        sx={{
          position: 'relative',
        }}
      >
        {data.map((dataPoint, index) => {
          const labelOptions = getLabelOptions?.({
            id: dataPoint.id,
            size: 12,
          });

          const label = dataPoint[yAxis.dataKey];

          return (
            <Stack
              key={dataPoint.id}
              direction="row"
              alignItems="center"
              flex={1}
            >
              {shouldShowLabel && (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.5}
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  width={`${labelWidthToUse}px`}
                  minWidth={`${labelWidthToUse}px`}
                  pr={shouldShowResizeHandle ? 2 : 0}
                >
                  {labelOptions?.icon && <Box>{labelOptions?.icon}</Box>}
                  <TypographyWithEllipsis
                    data-role="bar-chart-legend-label"
                    variant={BAR_LABEL_TYPOGRAPHY_VARIANT}
                    color="text.secondary"
                    shouldShowTooltip={false}
                    onMouseEnter={
                      onMouseEnter && !isResizing
                        ? (event) => onMouseEnter(event, index)
                        : undefined
                    }
                    onMouseLeave={onMouseLeave}
                  >
                    {getChartLabelIdOrName(String(label))}
                  </TypographyWithEllipsis>
                </Stack>
              )}
              {renderBar ? (
                renderBar({
                  dataPoint,
                  index,
                  onMouseEnter: !isResizing ? onMouseEnter : undefined,
                  onMouseLeave,
                })
              ) : (
                <Bar
                  dataPoint={dataPoint}
                  index={index}
                  xAxis={updatedXAxis}
                  color={getChartColor?.(index, yAxis['dataKey'])}
                  onMouseEnter={!isResizing ? onMouseEnter : undefined}
                  onMouseLeave={onMouseLeave}
                  formatBarValueLabel={formatBarValueLabel}
                />
              )}
            </Stack>
          );
        })}
        {shouldShowResizeHandle && (
          <BarChartResizeHandle
            onResize={onResizeLabelWidth}
            sxProps={{
              position: 'absolute',
              left: `calc(${labelWidthToUse}px - 20px)`,
              width: '18px',
              top: 0,
            }}
            onMouseDown={onResizeHandleMouseDown}
            onMouseUp={onResizeHandleMouseUp}
          />
        )}
      </Stack>
    </Box>
  );
};
